import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Input, message, Modal, Tabs } from 'antd';
import { CheckCircleFilled, CopyOutlined, PlusOutlined } from '@ant-design/icons';
import StepIndicator from '../components/OrderXV4/StepIndicator';
import StepIntake from '../components/OrderXV4/StepIntake';
import StepReviewMatch from '../components/OrderXV4/StepReviewMatch';
import StepCustomerShipping from '../components/OrderXV4/StepCustomerShipping';
import StepSummarySubmit from '../components/OrderXV4/StepSummarySubmit';
import ProcessingStatusCard from '../components/OrderXV4/ProcessingStatusCard';
import MyRequestsList from '../components/OrderXV4/MyRequestsList';
import RequestSourcePreviewModal from '../components/OrderXV4/RequestSourcePreviewModal';
import { useAuth } from '../contexts/AuthContext';
import { useSidebarContext } from '../contexts/SidebarContext';
import {
  getOrderGenieErpPricesBulk,
  getOrderGenieProductImageBaseUrl,
  getOrderGenieRequest,
  getOrderGenieRequestByCode,
  getOrderGenieSettings,
  matchOrderGenieRequest,
  recordOrderGenieDuplicateResolution,
  updateOrderGenieItems,
  updateOrderGenieSummary,
} from '../services/orderXApi';
import {
  DEFAULT_CHECKOUT_FLOW_CONFIG,
  buildFullItemsUpdatePayload,
  clearStoredPendingRequest,
  getStoredLastCheckout,
  getStoredPendingRequest,
  setStoredLastCheckout,
} from './orderXV4Utils';

const USER_ID_STORAGE_KEY = 'order_genie_v4_user_id';

// Shared default builder for `formValues` — used both on initial mount and when
// resuming a different request, so customer/ship-to fall back to the last-remembered
// convenience defaults rather than fabricating or carrying over stale selections from
// whatever the user had picked before switching away.
const buildDefaultFormValues = () => {
  const lastCheckout = getStoredLastCheckout();
  return {
    order_type: 'quote',
    customer: lastCheckout.customer,
    customer_search: lastCheckout.customer_search,
    customer_details: lastCheckout.customer_details,
    ship_to: lastCheckout.ship_to,
    ship_to_search: lastCheckout.ship_to_search,
    ship_to_details: lastCheckout.ship_to_details,
    bill_to_details: lastCheckout.bill_to_details,
    contact: lastCheckout.contact,
    contact_details: lastCheckout.contact_details,
    shipping_method: lastCheckout.shipping_method,
  };
};

// Keys of `formValues` that make up a customer/ship-to/contact selection —
// autosaved into the request's own summary_json (under "checkout") the same way
// Request Details fields already autosave, so resuming THIS request restores
// THIS request's own selection instead of whatever was last picked anywhere.
const CHECKOUT_FIELD_KEYS = [
  'customer', 'customer_search', 'customer_details',
  'ship_to', 'ship_to_search', 'ship_to_details',
  'bill_to_details', 'contact', 'contact_details', 'shipping_method',
];

// Reads back a previously-autosaved checkout selection from a request's
// summary_json.checkout, if any was ever saved for it. Returns null for
// requests that never reached that point (e.g. abandoned before Step 2), so
// callers can fall back to buildDefaultFormValues()'s last-used convenience
// defaults instead of showing blank fields.
const mapSummaryToCheckoutValues = (summary) => {
  const checkout = summary?.checkout;
  if (!checkout || typeof checkout !== 'object') return null;
  const values = { order_type: 'quote' };
  CHECKOUT_FIELD_KEYS.forEach((key) => { values[key] = checkout[key] ?? ''; });
  return values;
};

// Shared mapping from a request's summary_json (parsed at match time) to the
// headerFields shape — used by both the fresh-match path and the resume path so a
// future field addition/rename can't be fixed in one call site and not the other.
const mapSummaryToHeaderFields = (summary) => ({
  po_number: summary.po_number || summary.poNumber || '',
  required_date: summary.required_date || summary.requiredDate || '',
  project_name: summary.project_name || summary.projectName || '',
  order_notes: summary.order_notes || summary.orderNotes || '',
});

// The customer name/addresses AS WRITTEN ON THE SOURCE DOCUMENT itself (distinct
// from the ERP customer/ship-to the user picks in StepCustomerShipping) — read-only,
// informational, and already persisted server-side as part of the match-time
// summary_json write (see OrderGenieService's customer_name/customer_address/
// shipToAddress/billToAddress extraction), so this is purely a display mapping —
// nothing here needs its own autosave.
const mapSummaryToExtractedInfo = (summary) => ({
  customer_name: summary.customer_name || summary.customerName || '',
  customer_address: summary.customer_address || summary.customerAddress || '',
  ship_to_address: summary.ship_to_address || summary.shipToAddress || '',
  bill_to_address: summary.bill_to_address || summary.billToAddress || '',
});

const OrderGenieV4 = () => {
  const { account } = useAuth();
  const [userId, setUserId] = useState(() => {
    try { return localStorage.getItem(USER_ID_STORAGE_KEY) || ''; } catch (err) { return ''; }
  });
  const [step, setStep] = useState(0);
  const [maxReached, setMaxReached] = useState(0);
  const [activeTab, setActiveTab] = useState('wizard');

  // Review & Match (step 1) uses a wide split-screen layout (input document next
  // to extracted data/matches) — auto-collapse the app's left main menu while on
  // it to give that layout more room, and restore whatever the user had on the
  // way in when they leave.
  const { setCollapsed: setSidebarCollapsed } = useSidebarContext();
  useEffect(() => {
    if (activeTab !== 'wizard' || step !== 1) return undefined;
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(false);
  }, [activeTab, step, setSidebarCollapsed]);

  const [checkoutFlowConfig, setCheckoutFlowConfig] = useState(DEFAULT_CHECKOUT_FLOW_CONFIG);
  // 'dummy' (no real ERP pricing engine configured) is when the input document's
  // own Unit/Extended Price columns become a usable fallback in StepReviewMatch.
  const [pricingEngine, setPricingEngine] = useState('dummy');
  // null until settings load, so customer fields don't fire a P21 search for
  // an Eclipse account before the connector is known.
  const [erpConnector, setErpConnector] = useState(null);

  const [requestId, setRequestId] = useState(null);
  // The request's opaque public_share_token, exposed by the backend as
  // `request_code` -- used for the `?openRequest=` URL/deep-link instead of the
  // numeric `requestId` (a single BIGSERIAL sequence shared across every
  // account on the platform, not something that should show up in a URL).
  const [requestCode, setRequestCode] = useState(null);
  // The user_id that actually owns the currently active request -- not
  // necessarily the visible userId field's value, since Resume/Rerun Matches
  // can act on another owner's (e.g. the synthetic "email_inbox" user's)
  // request via an override without touching the field. Stashed in browser
  // history state so the back/forward URL-sync effect can restore the exact
  // request (including cross-owner ones) rather than guessing from whatever
  // the viewer's own userId field currently holds.
  const [activeRequestOwnerUserId, setActiveRequestOwnerUserId] = useState('');
  const [items, setItems] = useState([]);
  const [matches, setMatches] = useState([]);
  const [selectedByItem, setSelectedByItem] = useState({});
  const [qtyByItem, setQtyByItem] = useState({});
  const [productImageBaseUrl, setProductImageBaseUrl] = useState('');
  // Tracks the user's merge/keep-separate choice per duplicate_group_key so
  // StepReviewMatch's duplicate note doesn't re-prompt after a decision is made.
  const [resolvedDuplicateGroups, setResolvedDuplicateGroups] = useState({});
  // Running record of duplicate-group decisions (merge or keep-separate) for the
  // "Resolved" tab — separate from resolvedDuplicateGroups because a merged group's
  // key is deliberately cleared from that map (see applyMergeEntries) once its items
  // are gone, but the decision should still show up in the resolved history.
  const [duplicateResolutionLog, setDuplicateResolutionLog] = useState([]);
  const duplicateLogSeqRef = React.useRef(0);

  const [erpPriceByProductCode, setErpPriceByProductCode] = useState({});
  // Priced UOM per product code from the same bulk lookup (e.g. Eclipse UnitName).
  const [erpUomByProductCode, setErpUomByProductCode] = useState({});
  const [erpPriceLoadingByProductCode, setErpPriceLoadingByProductCode] = useState({});
  const erpPriceInFlightRef = React.useRef(new Set());
  // Codes already looked up (including empty/no-price). Prevents refetch loops when
  // /prices/bulk returns {"prices":{}}.
  const erpPriceAttemptedRef = React.useRef(new Set());
  const erpPriceCustomerRef = React.useRef('');

  const [headerFields, setHeaderFields] = useState({
    po_number: '', required_date: '', project_name: '', order_notes: '',
  });
  const [formValues, setFormValues] = useState(buildDefaultFormValues);
  const [extractedInfo, setExtractedInfo] = useState({
    customer_name: '', customer_address: '', ship_to_address: '', bill_to_address: '',
  });

  const [confirmation, setConfirmation] = useState(null);
  const [sourcePreviewOpen, setSourcePreviewOpen] = useState(false);

  // A request kicked off earlier (this tab, a previous session, or after closing the
  // browser) that may still be processing in the background — offered as a "resume"
  // banner rather than auto-jumping in, since the user may be here to start something new.
  const [pendingRequest, setPendingRequest] = useState(() => getStoredPendingRequest());
  const [resumingPending, setResumingPending] = useState(false);

  useEffect(() => {
    if (pendingRequest && String(pendingRequest.userId || '').trim().toLowerCase() !== userId.trim().toLowerCase()) {
      setPendingRequest(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const dismissPendingRequest = useCallback(() => {
    clearStoredPendingRequest();
    setPendingRequest(null);
    setResumingPending(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getOrderGenieSettings()
      .then((settings) => {
        if (cancelled) return;
        if (settings?.checkout_flow_config?.steps) {
          setCheckoutFlowConfig(settings.checkout_flow_config);
        }
        if (settings?.pricing_engine) {
          setPricingEngine(String(settings.pricing_engine).trim().toLowerCase() || 'dummy');
        }
        const connector = String(settings?.erp_connector || '').trim().toLowerCase();
        setErpConnector(connector === 'eclipse' ? 'eclipse' : 'p21');
      })
      .catch(() => {
        // fall back to default config
        if (!cancelled) setErpConnector('p21');
      });
    getOrderGenieProductImageBaseUrl()
      .then((url) => { if (!cancelled && url) setProductImageBaseUrl(url); })
      .catch(() => { /* fall back to no prefix — images render only when already absolute */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(USER_ID_STORAGE_KEY, userId); } catch (err) { /* ignore */ }
  }, [userId]);

  // Defaults the user ID field to the logged-in account's identity once it's
  // available, rather than leaving it blank for the user to type in themselves.
  // Only fires while the field is still empty — a manual edit or a previously
  // stored value (restored above) always wins over the account default.
  useEffect(() => {
    if (userId.trim()) return;
    const accountLabel = String(account?.username || account?.name || '').trim();
    if (accountLabel) setUserId(accountLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const loadErpPricesForCodes = useCallback((codes, options = {}) => {
    const customerIdErp = String(
      options.customer_id_erp
      ?? options.customerIdErp
      ?? formValues.customer
      ?? ''
    ).trim();
    const forceRefresh = Boolean(options.forceRefresh);
    if (forceRefresh || customerIdErp !== erpPriceCustomerRef.current) {
      erpPriceCustomerRef.current = customerIdErp;
      erpPriceInFlightRef.current = new Set();
      erpPriceAttemptedRef.current = new Set();
      setErpPriceByProductCode({});
      setErpUomByProductCode({});
      setErpPriceLoadingByProductCode({});
    }
    const missing = Array.from(new Set(
      (codes || []).map((code) => String(code || '').trim()).filter(Boolean)
    )).filter((code) => forceRefresh
      || (
        !(code in erpPriceByProductCode)
        && !erpPriceInFlightRef.current.has(code)
        && !erpPriceAttemptedRef.current.has(code)
      ));
    if (!missing.length) return;
    missing.forEach((code) => erpPriceInFlightRef.current.add(code));
    setErpPriceLoadingByProductCode((prev) => {
      const next = { ...prev };
      missing.forEach((code) => { next[code] = true; });
      return next;
    });
    getOrderGenieErpPricesBulk(missing, { customer_id_erp: customerIdErp })
      .then((resp) => {
        // If the "current request" context has moved on (e.g. the user resumed a
        // different request) since this lookup started, discard the result instead
        // of merging stale prices into the new request's review screen.
        if (erpPriceCustomerRef.current !== customerIdErp) return;
        const prices = resp?.prices && typeof resp.prices === 'object' ? resp.prices : {};
        const uoms = resp?.uoms && typeof resp.uoms === 'object' ? resp.uoms : {};
        if (forceRefresh) {
          setErpPriceByProductCode({ ...prices });
          setErpUomByProductCode({ ...uoms });
          return;
        }
        // Empty {"prices":{}} must not setState — that retriggers review effects
        // while codes stay "missing" and causes an infinite refetch loop.
        if (!Object.keys(prices).length) return;
        setErpPriceByProductCode((prev) => ({ ...prev, ...prices }));
        if (Object.keys(uoms).length) setErpUomByProductCode((prev) => ({ ...prev, ...uoms }));
      })
      .catch(() => { /* ignore — price line stays hidden for these codes */ })
      .finally(() => {
        missing.forEach((code) => {
          erpPriceInFlightRef.current.delete(code);
          erpPriceAttemptedRef.current.add(code);
        });
        setErpPriceLoadingByProductCode((prev) => {
          const next = { ...prev };
          missing.forEach((code) => { delete next[code]; });
          return next;
        });
      });
  }, [erpPriceByProductCode, formValues.customer]);

  const handleCustomerSelected = useCallback((customerId) => {
    const cid = String(customerId || '').trim();
    const codes = Array.from(new Set(
      (matches || [])
        .map((m) => String(m?.product_code || '').trim())
        .filter(Boolean)
    ));
    Object.values(selectedByItem || {}).forEach((code) => {
      const trimmed = String(code || '').trim();
      if (trimmed) codes.push(trimmed);
    });
    loadErpPricesForCodes(codes, { customer_id_erp: cid, forceRefresh: true });
  }, [loadErpPricesForCodes, matches, selectedByItem]);

  const handleProcessed = useCallback((newRequestId, fullRequest) => {
    clearStoredPendingRequest();
    setPendingRequest(null);
    setResumingPending(false);
    setRequestId(newRequestId);
    setRequestCode(fullRequest?.request?.request_code || null);
    setActiveRequestOwnerUserId(userId.trim());
    setItems(fullRequest?.items || []);
    setMatches(fullRequest?.matches || []);
    setHeaderFields(mapSummaryToHeaderFields(fullRequest?.request?.summary_json || {}));
    setExtractedInfo(mapSummaryToExtractedInfo(fullRequest?.request?.summary_json || {}));
    setResolvedDuplicateGroups({});
    setDuplicateResolutionLog([]);
    setStep(1);
    setMaxReached((prev) => Math.max(prev, 1));
  }, [userId]);

  // Guards against races on resume: a stale response (userId changed, tab left/unmounted,
  // or a second resume started before the first resolved) must never call setState.
  const mountedRef = React.useRef(true);
  const resumeTokenRef = React.useRef(0);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Resumes an in-process request found via the "My Requests" tab: hydrates
  // items/matches/headerFields from the backend exactly as handleProcessed does after
  // a fresh match, but always lands on Step 1 and pins maxReached to exactly 1 so the
  // step indicator can't expose steps beyond what this (possibly different) request has
  // actually completed. Customer/ship-to/contact are restored from THIS request's own
  // autosaved summary_json.checkout (see mapSummaryToCheckoutValues/CHECKOUT_FIELD_KEYS)
  // rather than the browser-wide "last used" cache, so switching between requests in My
  // Requests shows each request's own selection instead of whichever was picked most
  // recently anywhere. Requests that never reached that autosave (e.g. abandoned before
  // Step 2, or resumed for the first time after this feature shipped) fall back to the
  // last-used convenience defaults. Qty selections are still reset to normal defaults.
  // overrideUserId lets a deep-link (e.g. from the Email Inbox, where the request
  // is owned by the fixed synthetic "email_inbox" user rather than this browser's
  // own userId) resume a request without touching/clobbering the userId field.

  // Collapses one or more confirmed duplicate groups into a single surviving
  // item per group (the first member), carrying each merged preview's summed
  // qty and field values. No 'merged' resolution is recorded: once merged, a
  // group has exactly one surviving member and groupDuplicateItems never
  // returns it again. Groups are disjoint (each item belongs to at most one
  // duplicate_group_key), so entries can be applied in a single state update.
  // Declared above handleResumeRequest since it's referenced in that hook's deps.
  const applyMergeEntries = useCallback((entries) => {
    if (!entries || !entries.length) return;
    const removedIdsAll = [];
    const survivingUpdates = {};
    entries.forEach(({ mergedPreview, memberIds }) => {
      const [survivingId, ...removedIds] = memberIds;
      survivingUpdates[survivingId] = mergedPreview;
      removedIdsAll.push(...removedIds);
    });
    setItems((prev) => prev
      .filter((item) => !removedIdsAll.includes(String(item.id || '')))
      .map((item) => {
        const key = String(item.id || '');
        const mergedPreview = survivingUpdates[key];
        if (!mergedPreview) return item;
        return {
          ...item,
          normalized_parts: {
            ...item.normalized_parts,
            part_number: mergedPreview.part_number,
            description: mergedPreview.description,
            manufacturer: mergedPreview.manufacturer,
            manufacturer_part_number: mergedPreview.manufacturer_part_number,
            customer_part_number: mergedPreview.customer_part_number,
            notes: mergedPreview.notes,
            uom: mergedPreview.uom,
          },
        };
      }));
    setQtyByItem((prev) => {
      const next = { ...prev };
      removedIdsAll.forEach((id) => { delete next[id]; });
      Object.entries(survivingUpdates).forEach(([survivingId, mergedPreview]) => {
        next[survivingId] = mergedPreview.qty;
      });
      return next;
    });
    setSelectedByItem((prev) => {
      const next = { ...prev };
      removedIdsAll.forEach((id) => { delete next[id]; });
      return next;
    });
    setMatches((prev) => (prev || []).filter((m) => !removedIdsAll.includes(String(m?.item_id ?? ''))));
    // Clear any stale 'separate' resolution under these keys so a future group that
    // happens to reuse one (e.g. re-parsing the same request) isn't suppressed.
    setResolvedDuplicateGroups((prev) => {
      const groupKeys = entries.map((e) => e.groupKey);
      if (!groupKeys.some((k) => k in prev)) return prev;
      const next = { ...prev };
      groupKeys.forEach((k) => { delete next[k]; });
      return next;
    });
    setDuplicateResolutionLog((prev) => [
      ...prev,
      ...entries.map((e) => {
        duplicateLogSeqRef.current += 1;
        return {
          key: `merged-${e.groupKey}-${duplicateLogSeqRef.current}`,
          mode: 'merged',
          lineLabel: e.lineLabel || '',
          detail: `Merged into 1 line — qty ${e.mergedPreview?.qty ?? ''}`,
          actorUserId: e.actorUserId || '',
          createdAt: e.createdAt || '',
        };
      }),
    ]);
  }, []);

  // Shared hydration for both resume paths below -- the only difference between
  // them is how `fullRequest` gets fetched (by numeric id vs. by opaque code);
  // everything after that fetch is identical.
  const applyResumedRequest = useCallback((fullRequest, targetRequestId, trimmedUserId) => {
    const resumedItems = fullRequest?.items || [];
    const resumedMatches = fullRequest?.matches || [];
    if (!resumedItems.length && !resumedMatches.length) {
      throw new Error('This request has no items to resume.');
    }
    clearStoredPendingRequest();
    setPendingRequest(null);
    setResumingPending(false);
    setRequestId(targetRequestId);
    setRequestCode(fullRequest?.request?.request_code || null);
    setActiveRequestOwnerUserId(trimmedUserId);
    setItems(resumedItems);
    setMatches(resumedMatches);
    const resumedSummary = fullRequest?.request?.summary_json || {};
    setHeaderFields(mapSummaryToHeaderFields(resumedSummary));
    setExtractedInfo(mapSummaryToExtractedInfo(resumedSummary));
    setSelectedByItem({});
    setQtyByItem({});
    // Replay previously-persisted duplicate resolutions against the freshly loaded
    // items so a merge/keep-separate decision made before navigating away doesn't
    // reappear as unresolved -- see recordOrderGenieDuplicateResolution / persistDuplicateResolution.
    const persistedResolutions = fullRequest?.duplicate_resolutions || [];
    const keptSeparateResolved = {};
    const keptSeparateLog = [];
    const mergedEntries = [];
    persistedResolutions.forEach((row) => {
      if (row.action === 'kept_separate') {
        keptSeparateResolved[row.group_key] = 'separate';
        keptSeparateLog.push({
          key: `resolved-${row.id}`,
          mode: 'separate',
          lineLabel: row.line_label || '',
          detail: 'Kept separate',
          actorUserId: row.actor_user_id || '',
          createdAt: row.created_at || '',
        });
      } else if (row.action === 'merged') {
        mergedEntries.push({
          groupKey: row.group_key,
          mergedPreview: row.merged_preview || {},
          memberIds: (row.member_ids || []).map((id) => String(id)),
          lineLabel: row.line_label || '',
          actorUserId: row.actor_user_id || '',
          createdAt: row.created_at || '',
        });
      }
    });
    setResolvedDuplicateGroups(keptSeparateResolved);
    setDuplicateResolutionLog(keptSeparateLog);
    if (mergedEntries.length) applyMergeEntries(mergedEntries);
    setErpPriceByProductCode({});
    setErpUomByProductCode({});
    setErpPriceLoadingByProductCode({});
    erpPriceInFlightRef.current = new Set();
    erpPriceAttemptedRef.current = new Set();
    erpPriceCustomerRef.current = '';
    setFormValues(mapSummaryToCheckoutValues(resumedSummary) || buildDefaultFormValues());
    setStep(1);
    setMaxReached(1);
    setActiveTab('wizard');
  }, [applyMergeEntries]);

  const handleResumeRequest = useCallback(async (targetRequestId, overrideUserId) => {
    const usingOverride = overrideUserId != null;
    const trimmedUserId = usingOverride ? String(overrideUserId).trim() : userId.trim();
    if (!trimmedUserId || targetRequestId == null) return;
    const token = resumeTokenRef.current + 1;
    resumeTokenRef.current = token;
    try {
      const fullRequest = await getOrderGenieRequest(targetRequestId, trimmedUserId);
      if (
        !mountedRef.current
        || resumeTokenRef.current !== token
        || (!usingOverride && userId.trim() !== trimmedUserId)
      ) return;
      applyResumedRequest(fullRequest, targetRequestId, trimmedUserId);
    } catch (err) {
      if (!mountedRef.current || resumeTokenRef.current !== token) return;
      message.error(err?.message || 'Unable to load request');
    }
  }, [userId, applyResumedRequest]);

  // Same as handleResumeRequest, but for the `?openRequest=<code>` deep link
  // (Email Inbox links, browser Back/Forward) -- resolves the request's opaque
  // code to its numeric id server-side (GET /requests/by-code/{code}) instead
  // of trusting a numeric id straight from the URL.
  const handleResumeRequestByCode = useCallback(async (code, overrideUserId) => {
    const usingOverride = overrideUserId != null;
    const trimmedUserId = usingOverride ? String(overrideUserId).trim() : userId.trim();
    const trimmedCode = String(code || '').trim();
    if (!trimmedUserId || !trimmedCode) return;
    const token = resumeTokenRef.current + 1;
    resumeTokenRef.current = token;
    try {
      const fullRequest = await getOrderGenieRequestByCode(trimmedCode, trimmedUserId);
      if (
        !mountedRef.current
        || resumeTokenRef.current !== token
        || (!usingOverride && userId.trim() !== trimmedUserId)
      ) return;
      const resolvedRequestId = fullRequest?.request?.id;
      if (resolvedRequestId == null) throw new Error('Request not found.');
      applyResumedRequest(fullRequest, resolvedRequestId, trimmedUserId);
    } catch (err) {
      if (!mountedRef.current || resumeTokenRef.current !== token) return;
      message.error(err?.message || 'Unable to load request');
    }
  }, [userId, applyResumedRequest]);

  // Re-runs product matching for a request already sitting in "My Requests" without
  // re-parsing its source file(s) — the extraction stage already produced the items
  // in order_genie_items, so this only redoes the OpenSearch/Solr matching step
  // against those existing rows (POST /requests/{id}/match), then reloads the
  // request the same way handleResumeRequest does so Step 1 shows the fresh matches.
  const handleRerunMatches = useCallback(async (targetRequestId, overrideUserId) => {
    const usingOverride = overrideUserId != null;
    const trimmedUserId = usingOverride ? String(overrideUserId).trim() : userId.trim();
    if (!trimmedUserId || targetRequestId == null) return;
    try {
      await matchOrderGenieRequest(targetRequestId, { user_id: trimmedUserId });
    } catch (err) {
      message.error(err?.message || 'Unable to rerun matches');
      return;
    }
    await handleResumeRequest(targetRequestId, overrideUserId);
  }, [userId, handleResumeRequest]);

  // Deep-link: read ?openRequest=<code> on mount and auto-resume that request
  // into the preview (Step 1 / Review Match), e.g. from an Email Inbox link.
  // <code> is the request's opaque request_code (its public_share_token), not
  // the numeric id -- resolved server-side via GET /requests/by-code/{code} so
  // the URL never carries the raw BIGSERIAL id (one sequence shared across
  // every account on the platform). Email-originated requests are owned by the
  // fixed synthetic "email_inbox" user, so the visible userId field is switched
  // to it too — otherwise the wizard's own `{userId.trim() && ...}` render gate
  // hides every step even once the request data has loaded, since it never had
  // a reason to treat itself as "logged in".
  // deepLinkResumeInFlightRef tells the URL-sync effect below to stand down while
  // this is in flight — requestId/activeTab haven't caught up with the URL's
  // ?openRequest=<code> yet, so reconciling against them now would strip it
  // right back out before the fetch it's waiting on even resolves.
  const deepLinkResumeInFlightRef = React.useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const reqCode = params.get('openRequest');
    if (!reqCode) return;
    deepLinkResumeInFlightRef.current = true;
    setUserId('email_inbox');
    handleResumeRequestByCode(reqCode, 'email_inbox').finally(() => {
      deepLinkResumeInFlightRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keeps the URL in sync with the active tab and, while on the wizard tab,
  // whichever request is open — every request/tab combination gets its own
  // bookmarkable/shareable/refresh-safe URL, and each transition is pushed as
  // its own history entry (via history.state, not just the query string) so
  // the browser's Back/Forward buttons step through them: e.g. resume a
  // request, switch to My Requests, hit Back — that returns to the request
  // (owner and all) rather than leaving the address bar pointing at the list.
  // firstUrlSyncDoneRef skips exactly the mount-time pass: see
  // deepLinkResumeInFlightRef's comment above for why acting on it early would
  // be wrong, and once the deep-link resume (if any) finishes, requestId
  // already matches the URL, so there's nothing to reconcile at that point either.
  const firstUrlSyncDoneRef = React.useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (deepLinkResumeInFlightRef.current) return;
    if (!firstUrlSyncDoneRef.current) {
      firstUrlSyncDoneRef.current = true;
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const currentTab = params.get('tab') === 'myRequests' ? 'myRequests' : 'wizard';
    const currentRequest = params.get('openRequest');
    const desiredRequest = activeTab === 'wizard' && requestId != null ? requestCode : null;
    if (activeTab === currentTab && desiredRequest === currentRequest) return;

    const nextParams = new URLSearchParams();
    if (activeTab === 'myRequests') {
      nextParams.set('tab', 'myRequests');
    } else if (desiredRequest) {
      nextParams.set('openRequest', desiredRequest);
    }
    const query = nextParams.toString();
    const nextUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    const historyState = { tab: activeTab, requestCode: desiredRequest, ownerUserId: desiredRequest ? activeRequestOwnerUserId : null };
    window.history.pushState(historyState, '', nextUrl);
  }, [requestId, requestCode, activeTab, activeRequestOwnerUserId]);

  // Restores tab/request state on browser Back/Forward navigation, reversing
  // whatever the URL-sync effect above pushed. Prefers history.state (the
  // exact owner user_id for the request that was active at that point in
  // history) over re-deriving from the URL, since the visible userId field
  // may not match a cross-owner (e.g. email_inbox) request's actual owner.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handlePopState = (event) => {
      const state = event.state;
      const params = new URLSearchParams(window.location.search);
      const tab = state?.tab || (params.get('tab') === 'myRequests' ? 'myRequests' : 'wizard');
      const reqCode = state && 'requestCode' in state ? state.requestCode : params.get('openRequest');
      if (tab === 'myRequests') {
        setActiveTab('myRequests');
        return;
      }
      if (reqCode) {
        const ownerUserId = state?.ownerUserId || undefined;
        handleResumeRequestByCode(reqCode, ownerUserId);
      } else {
        setActiveTab('wizard');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleResumeRequestByCode]);

  const handleSelectMatch = useCallback((itemKey, productCode) => {
    setSelectedByItem((prev) => ({ ...prev, [itemKey]: productCode }));
  }, []);

  // Adds a product picked from the "search for a different product" flow as a
  // selectable match for a line item, then selects it — used when the AI-suggested
  // matches don't include the right product.
  const handleAddManualMatch = useCallback((itemKey, product) => {
    const productCode = String(product?.product_code || '').trim();
    if (!productCode) return;
    setMatches((prev) => {
      const already = (prev || []).some(
        (m) => String(m?.item_id ?? '') === itemKey && m.product_code === productCode
      );
      if (already) return prev;
      return [
        ...(prev || []),
        {
          item_id: itemKey,
          product_code: productCode,
          match: product,
          reasoning: 'Manually selected via product search',
          manual: true,
        },
      ];
    });
    setSelectedByItem((prev) => ({ ...prev, [itemKey]: productCode }));
    loadErpPricesForCodes([productCode]);
  }, [loadErpPricesForCodes]);

  const handleHeaderFieldChange = useCallback((key, value) => {
    setHeaderFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Explicit (not debounced/autosaved) save for a single extracted-info field
  // (customer_name/customer_address/ship_to_address/bill_to_address) -- these
  // came from AI extraction with no prior edit affordance at all, unlike
  // headerFields/formValues which already had inline inputs. Saves immediately
  // on the user's explicit click (see ExtractedDocumentInfoCard's per-field edit
  // icon in StepReviewMatch.jsx), awaited by the caller so a failure surfaces
  // inline instead of silently reverting on next resume. Written under the same
  // snake_case key mapSummaryToExtractedInfo checks first, so it round-trips.
  const handleExtractedInfoFieldChange = useCallback(async (key, value) => {
    if (!requestId || !userId.trim()) {
      throw new Error('Save is unavailable right now — please reload and try again.');
    }
    await updateOrderGenieSummary(requestId, {
      user_id: userId.trim(),
      summary: { [key]: value },
      merge: true,
    });
    setExtractedInfo((prev) => ({ ...prev, [key]: value }));
  }, [requestId, userId]);

  // Debounced autosave so Request Details (PO number, required date, project name,
  // order notes) survive navigating away/resuming before the final submit step —
  // merges into summary_json rather than replacing it wholesale, and intentionally
  // skips the quotes-table write that StepSummarySubmit's final save performs.
  const headerFieldsSaveTimerRef = React.useRef(null);
  useEffect(() => {
    if (!requestId || !userId.trim()) return undefined;
    headerFieldsSaveTimerRef.current = setTimeout(() => {
      updateOrderGenieSummary(requestId, {
        user_id: userId.trim(),
        summary: headerFields,
        merge: true,
      }).catch(() => { /* best-effort autosave; final submit still persists the full summary */ });
    }, 800);
    return () => clearTimeout(headerFieldsSaveTimerRef.current);
  }, [headerFields, requestId, userId]);

  // Debounced autosave for the customer/ship-to/contact selection, mirroring the
  // headerFields autosave above — persisted under summary_json.checkout so that
  // resuming THIS request (see handleResumeRequest/mapSummaryToCheckoutValues)
  // restores THIS request's own selection instead of the browser-wide "last
  // used" cache (getStoredLastCheckout/setStoredLastCheckout), which is still
  // written to separately below as a convenience default for brand-new requests.
  const checkoutFieldsSaveTimerRef = React.useRef(null);
  useEffect(() => {
    if (!requestId || !userId.trim()) return undefined;
    checkoutFieldsSaveTimerRef.current = setTimeout(() => {
      const checkout = {};
      CHECKOUT_FIELD_KEYS.forEach((key) => { checkout[key] = formValues[key]; });
      updateOrderGenieSummary(requestId, {
        user_id: userId.trim(),
        summary: { checkout },
        merge: true,
      }).catch(() => { /* best-effort autosave; final submit still persists the full summary */ });
    }, 800);
    return () => clearTimeout(checkoutFieldsSaveTimerRef.current);
  }, [formValues, requestId, userId]);

  const handleFormValuesChange = useCallback((patch) => {
    setFormValues((prev) => {
      const next = { ...prev, ...patch };
      const shouldPersist = [
        'customer_details',
        'ship_to_details',
        'bill_to_details',
        'contact_details',
        'shipping_method',
      ].some((key) => Object.prototype.hasOwnProperty.call(patch, key) && patch[key]);
      if (shouldPersist) setStoredLastCheckout(next);
      return next;
    });
  }, []);

  const goToStep = useCallback((targetStep) => {
    if (targetStep <= maxReached) setStep(targetStep);
  }, [maxReached]);

  const handleContinueFromReview = useCallback(() => {
    setStep(2);
    setMaxReached((prev) => Math.max(prev, 2));
  }, []);

  // Saves an edit made in EditItemDetailsModal (StepReviewMatch's "Edit Details"
  // affordance) for a single line item. update_items (order_genie_service.py
  // ~13724) is a full-list sync -- it DELETES every existing item/match/cart
  // row whose id isn't present in the incoming `items` array -- so this must
  // send the CURRENT FULL items array via buildFullItemsUpdatePayload, not
  // just the one edited item (a review-caught data-loss bug: an earlier
  // version of this handler sent a single-item array, which would have
  // silently deleted every other line item on save). Only the target item's
  // fields change; every other item passes through unchanged. Deliberately
  // does not update `items` until the save succeeds, so a failed save
  // (network/5xx) leaves state untouched; the caller (EditItemDetailsModal,
  // via StepReviewMatch) awaits this and shows the rejection inline instead
  // of closing.
  const handleEditItemFields = useCallback(async (itemId, updatedFields) => {
    const key = String(itemId ?? '');
    const currentItem = items.find((it) => String(it.id || '') === key);
    if (!currentItem) {
      throw new Error('This line item is no longer available — please refresh and try again.');
    }
    const payloadItems = buildFullItemsUpdatePayload(items, itemId, updatedFields);
    const data = await updateOrderGenieItems(requestId, {
      user_id: userId.trim(),
      items: payloadItems,
    });
    const savedItemsById = new Map(
      (Array.isArray(data?.items) ? data.items : []).map((it) => [String(it.id || ''), it])
    );
    setItems((prev) => prev.map((it) => {
      const itKey = String(it.id || '');
      const saved = savedItemsById.get(itKey);
      if (saved) return saved;
      if (itKey === key) {
        const editedPayload = payloadItems.find((p) => String(p.id || '') === key);
        return editedPayload ? { ...it, normalized_parts: editedPayload.normalized_parts } : it;
      }
      return it;
    }));
  }, [items, requestId, userId]);

  // Persists the resolution decision (who/when) so it survives navigating away and
  // resuming the request later -- best-effort: a failed save keeps the local
  // resolution applied but warns the user it may not stick after they leave.
  const persistDuplicateResolution = useCallback((groupKey, action, lineLabel, memberIds, mergedPreview) => {
    if (requestId == null) return;
    recordOrderGenieDuplicateResolution(requestId, {
      user_id: userId.trim(),
      group_key: groupKey,
      action,
      line_label: lineLabel || '',
      member_ids: memberIds || [],
      merged_preview: mergedPreview || {},
    }).catch((err) => {
      message.error(err?.message || 'Unable to save this duplicate resolution -- it may not persist if you leave this request.');
    });
  }, [requestId, userId]);

  const handleMergeDuplicateGroup = useCallback((groupKey, mergedPreview, memberIds, lineLabel) => {
    const actorUserId = userId.trim();
    const createdAt = new Date().toISOString();
    applyMergeEntries([{ groupKey, mergedPreview, memberIds, lineLabel, actorUserId, createdAt }]);
    persistDuplicateResolution(groupKey, 'merged', lineLabel, memberIds, mergedPreview);
  }, [applyMergeEntries, persistDuplicateResolution, userId]);

  const handleBulkMergeDuplicateGroups = useCallback((entries) => {
    const actorUserId = userId.trim();
    const createdAt = new Date().toISOString();
    applyMergeEntries((entries || []).map((e) => ({ ...e, actorUserId, createdAt })));
    (entries || []).forEach((e) => {
      persistDuplicateResolution(e.groupKey, 'merged', e.lineLabel, e.memberIds, e.mergedPreview);
    });
  }, [applyMergeEntries, persistDuplicateResolution, userId]);

  const handleKeepDuplicatesSeparate = useCallback((groupKey, lineLabel) => {
    const actorUserId = userId.trim();
    const createdAt = new Date().toISOString();
    setResolvedDuplicateGroups((prev) => ({ ...prev, [groupKey]: 'separate' }));
    duplicateLogSeqRef.current += 1;
    setDuplicateResolutionLog((prev) => [
      ...prev,
      {
        key: `separate-${groupKey}-${duplicateLogSeqRef.current}`,
        mode: 'separate',
        lineLabel: lineLabel || '',
        detail: 'Kept separate',
        actorUserId,
        createdAt,
      },
    ]);
    persistDuplicateResolution(groupKey, 'kept_separate', lineLabel);
  }, [persistDuplicateResolution, userId]);

  const handleBulkKeepDuplicatesSeparate = useCallback((groupKeys, lineLabelByKey) => {
    const actorUserId = userId.trim();
    const createdAt = new Date().toISOString();
    setResolvedDuplicateGroups((prev) => {
      const next = { ...prev };
      (groupKeys || []).forEach((k) => { next[k] = 'separate'; });
      return next;
    });
    setDuplicateResolutionLog((prev) => [
      ...prev,
      ...(groupKeys || []).map((k) => {
        duplicateLogSeqRef.current += 1;
        return {
          key: `separate-${k}-${duplicateLogSeqRef.current}`,
          mode: 'separate',
          lineLabel: lineLabelByKey?.[k] || '',
          detail: 'Kept separate',
          actorUserId,
          createdAt,
        };
      }),
    ]);
    (groupKeys || []).forEach((k) => {
      persistDuplicateResolution(k, 'kept_separate', lineLabelByKey?.[k]);
    });
  }, [persistDuplicateResolution, userId]);

  const handleContinueFromCustomer = useCallback(() => {
    setStoredLastCheckout(formValues);
    setStep(3);
    setMaxReached((prev) => Math.max(prev, 3));
  }, [formValues]);

  const handleSubmitted = useCallback((info) => {
    setConfirmation(info);
  }, []);

  const handleStartNew = useCallback(() => {
    clearStoredPendingRequest();
    setPendingRequest(null);
    setResumingPending(false);
    setRequestId(null);
    setRequestCode(null);
    setActiveRequestOwnerUserId('');
    setItems([]);
    setMatches([]);
    setSelectedByItem({});
    setQtyByItem({});
    setResolvedDuplicateGroups({});
    setDuplicateResolutionLog([]);
    setErpPriceByProductCode({});
    setErpUomByProductCode({});
    setErpPriceLoadingByProductCode({});
    setHeaderFields({ po_number: '', required_date: '', project_name: '', order_notes: '' });
    setFormValues((prev) => ({ ...prev, shipping_method: '', order_type: 'quote' }));
    setConfirmation(null);
    setStep(0);
    setMaxReached(0);
  }, []);

  // Lets the user abandon the request they're currently working through (steps
  // 1-3) and jump back to intake to upload something else — without this, the
  // only way back to step 0 was finishing or refreshing the page. The in-progress
  // request itself is untouched server-side; this only resets local wizard state.
  const handleStartNewMidFlow = useCallback(() => {
    Modal.confirm({
      title: 'Start a new request?',
      content: `Request #${requestId} will stay saved, but any unsaved selections on this screen will be discarded.`,
      okText: 'Start new request',
      onOk: handleStartNew,
    });
  }, [requestId, handleStartNew]);

  if (confirmation) {
    const isOrder = confirmation.orderType === 'order';
    const lineItems = confirmation.lineItems || [];
    const matchedItems = lineItems.filter((li) => li.matched);
    const unmatchedItems = lineItems.filter((li) => !li.matched);
    const totalUnits = matchedItems.reduce((sum, li) => sum + (Number(li.qty) || 0), 0);

    return (
      <div className="container py-5 text-center">
        <Card className="shadow-sm border-0 mx-auto" style={{ maxWidth: 480 }}>
          <h3 className="mb-2">
            {confirmation.orderType === 'order' ? 'Order Submitted' : 'Quote Saved'}
          </h3>
          {confirmation.orderNumber ? (
            <p className="mb-2">
              ERP {confirmation.orderType === 'order' ? 'Order' : 'Quote'} #:
              {' '}<strong>{confirmation.orderNumber}</strong>
            </p>
          ) : null}
          <p className="text-muted mb-4">Request #{confirmation.requestId}</p>
          <Button type="primary" onClick={handleStartNew}>Start New Request</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">OrderX v4</h2>
        <div style={{ width: 220 }}>
          <Input
            size="small"
            placeholder="Your user ID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={[
          { key: 'wizard', label: 'New / In Progress' },
          { key: 'myRequests', label: 'My Requests' },
        ]}
      />

      {activeTab === 'myRequests' && (
        <>
          {!userId.trim() && (
            <div className="alert alert-warning">Enter your user ID above to see your requests.</div>
          )}
          {userId.trim() && (
            <MyRequestsList
              userId={userId.trim()}
              active={activeTab === 'myRequests'}
              onResume={handleResumeRequest}
              onRerunMatches={handleRerunMatches}
            />
          )}
        </>
      )}

      {activeTab === 'wizard' && (
        <>
          <div className="d-flex justify-content-between align-items-start">
            <StepIndicator current={step} maxReached={maxReached} onJump={goToStep} />
            <div className="d-flex gap-2">
              {requestId != null && (
                <Button size="small" onClick={() => setSourcePreviewOpen(true)}>Preview Original Document</Button>
              )}
              {requestId != null && step > 0 && (
                <Button size="small" onClick={handleStartNewMidFlow}>Start New Request</Button>
              )}
            </div>
          </div>

          <RequestSourcePreviewModal
            requestId={requestId}
            userId={userId.trim()}
            open={sourcePreviewOpen}
            onClose={() => setSourcePreviewOpen(false)}
          />

          {!userId.trim() && (
            <div className="alert alert-warning">Enter your user ID above to start a request.</div>
          )}

          {userId.trim() && step === 0 && pendingRequest && !resumingPending && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                Request <strong>#{pendingRequest.requestId}</strong>
                {pendingRequest.requestName ? ` (${pendingRequest.requestName})` : ''} may still be processing in the background.
              </div>
              <div className="d-flex gap-2">
                <Button size="small" type="primary" onClick={() => setResumingPending(true)}>Resume</Button>
                <Button size="small" onClick={dismissPendingRequest}>Dismiss</Button>
              </div>
            </div>
          )}

          {userId.trim() && step === 0 && resumingPending && pendingRequest && (
            <ProcessingStatusCard
              requestId={pendingRequest.requestId}
              userId={userId.trim()}
              requestName={pendingRequest.requestName}
              onReady={(fullRequest) => handleProcessed(pendingRequest.requestId, fullRequest)}
              onFailed={() => dismissPendingRequest()}
              onLeaveInBackground={() => setResumingPending(false)}
              onCancelled={() => dismissPendingRequest()}
            />
          )}

          {userId.trim() && step === 0 && !resumingPending && (
            <StepIntake
              userId={userId.trim()}
              onProcessed={handleProcessed}
              formValues={formValues}
              onFormValuesChange={handleFormValuesChange}
              onCustomerSelected={handleCustomerSelected}
              erpConnector={erpConnector}
            />
          )}

          {userId.trim() && step === 1 && (
            <StepReviewMatch
              items={items}
              matches={matches}
              selectedByItem={selectedByItem}
              onSelectMatch={handleSelectMatch}
              qtyByItem={qtyByItem}
              onQtyChange={(key, qty) => setQtyByItem((prev) => ({ ...prev, [key]: qty }))}
              erpPriceByProductCode={erpPriceByProductCode}
              erpPriceLoadingByProductCode={erpPriceLoadingByProductCode}
              erpUomByProductCode={erpUomByProductCode}
              loadErpPricesForCodes={loadErpPricesForCodes}
              headerFields={headerFields}
              onHeaderFieldChange={handleHeaderFieldChange}
              checkoutFlowConfig={checkoutFlowConfig}
              formValues={formValues}
              onContinue={handleContinueFromReview}
              productImageBaseUrl={productImageBaseUrl}
              onAddManualMatch={handleAddManualMatch}
              pricingEngine={pricingEngine}
              resolvedDuplicateGroups={resolvedDuplicateGroups}
              onMergeDuplicateGroup={handleMergeDuplicateGroup}
              onKeepDuplicatesSeparate={handleKeepDuplicatesSeparate}
              onBulkMergeDuplicateGroups={handleBulkMergeDuplicateGroups}
              onBulkKeepDuplicatesSeparate={handleBulkKeepDuplicatesSeparate}
              duplicateResolutionLog={duplicateResolutionLog}
              onEditItemFields={handleEditItemFields}
              requestId={requestId}
              userId={userId.trim()}
              extractedInfo={extractedInfo}
              onExtractedInfoFieldChange={handleExtractedInfoFieldChange}
            />
          )}

          {userId.trim() && step === 2 && (
            <StepCustomerShipping
              checkoutFlowConfig={checkoutFlowConfig}
              formValues={formValues}
              onFormValuesChange={handleFormValuesChange}
              onBack={() => setStep(1)}
              onContinue={handleContinueFromCustomer}
              onCustomerSelected={handleCustomerSelected}
              items={items}
              matches={matches}
              selectedByItem={selectedByItem}
              qtyByItem={qtyByItem}
              erpPriceByProductCode={erpPriceByProductCode}
              pricingEngine={pricingEngine}
              erpConnector={erpConnector}
            />
          )}

          {userId.trim() && step === 3 && (
            <StepSummarySubmit
              requestId={requestId}
              userId={userId.trim()}
              items={items}
              matches={matches}
              selectedByItem={selectedByItem}
              qtyByItem={qtyByItem}
              erpPriceByProductCode={erpPriceByProductCode}
              erpPriceLoadingByProductCode={erpPriceLoadingByProductCode}
              erpUomByProductCode={erpUomByProductCode}
              headerFields={headerFields}
              formValues={formValues}
              onFormValuesChange={handleFormValuesChange}
              checkoutFlowConfig={checkoutFlowConfig}
              onBack={() => setStep(2)}
              onSubmitted={handleSubmitted}
              pricingEngine={pricingEngine}
              productImageBaseUrl={productImageBaseUrl}
              erpConnector={erpConnector}
            />
          )}
        </>
      )}
    </div>
  );
};

export default OrderGenieV4;
